/* eslint-disable react/prop-types */
/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import {
  getEstadosGlobales,
} from 'api/catalogos/index';
import {
  getDescuentosRangosSalariales,
  getDescuentoRangoSalarial,
  postDescuentoRangoSalarial,
  patchDescuentoRangoSalarial,
  deleteDescuentoRangoSalarial,
} from 'api/nominas/descuentos/descuentosRangosSalariales';
import { checkToken } from 'api/utils';
import PercentageInput from 'components/PercentageInput';
import percentFormatter from 'utils/percentFormatter';
import currencyFormatter from 'utils/currencyFormatter';
import CurrencyInput from 'components/CurrencyInput';

export const descuentoDeduccionPermission = {
  permissionModel: 'descuentodeduccion',
  catalogsModels: [],
  tabsModels: [],
};

// eslint-disable-next-line no-unused-vars
const DescuentosRangosSalariales = ({ permission, selected, estatusAutorizadoInactivo }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);

  const formInstances = [
    form,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getDescuentosRangosSalariales({ descuento: selected });
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getDescuentosRangosSalariales({ descuento: selected }),
      ]);
      if (mounted) {
        setEstados(_estados);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _descuento = await getDescuentoRangoSalarial(match.id);
    setTimeout(() => {
      if (_descuento) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_descuento));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchDescuentoRangoSalarial(key, { ...values, descuento: selected }) : postDescuentoRangoSalarial({ ...values, descuento: selected }));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteDescuentoRangoSalarial(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Límite Inferior',
      dataIndex: 'limite_inferior',
      key: 'limite_inferior',
      width: 200,
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Límite Superior',
      dataIndex: 'limite_superior',
      key: 'limite_superior',
      width: 200,
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: '% de Importe Variable',
      dataIndex: 'porcentaje_predeterminado_importe_variable',
      key: 'porcentaje_predeterminado_importe_variable',
      width: 200,
      render: (val) => percentFormatter(val || 0),
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    contratacion: [requiredRule],
    estatus: [requiredRule],
    limite_superior: [
      requiredRule,
      {
        validator: async (rule, val) => {
          if (val < form.getFieldValue('limite_inferior')) {
            throw new Error('Límite Superior no puede ser menor a Límite Inferior');
          }
        },
      },
    ],
    limite_inferior: [
      requiredRule,
      {
        validator: async (rule, val) => {
          if (val > form.getFieldValue('limite_superior')) {
            throw new Error('Límite Inferior no puede ser mayor a Límite Superior');
          }
        },
      },
    ],
    porcentaje_importe_variable: [
      requiredRule,
      {
        validator: async (rule, val) => {
          if (val > 100) {
            throw new Error('El porcentaje no puede ser mayor a 100');
          }
        },
      },
    ],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          allowAdd={!estatusAutorizadoInactivo}
          allowEdit={!estatusAutorizadoInactivo}
          allowDelete={!estatusAutorizadoInactivo}
          allowExportPDF={false}
          permission={permission}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Rango Salarial`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
            )}
          onCancel={onCancel}
          forceRender
          width="40%"
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
          >
            <Col xs={24} sm={24} md={24}>
              <CurrencyInput
                name="limite_inferior"
                label="Límite Inferior"
                rules={rules.limite_inferior}
                hasFeedback
              />
            </Col>
            <Col xs={24} sm={24} md={24}>
              <CurrencyInput
                name="limite_superior"
                label="Límite Superior"
                rules={rules.limite_superior}
                hasFeedback
              />
            </Col>
            <Col xs={24} sm={24} md={24}>
              <PercentageInput
                name="porcentaje_predeterminado_importe_variable"
                label="% de Importe Variable"
                rules={rules.porcentaje_importe_variable}
                hasFeedback
              />
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                rules={rules.estatus}
                hasFeedback
                initialValue={1}
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={estados}
                  disabled={selectedRowKeys.length <= 0}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Rango Salarial Límite Inferior ${form.getFieldValue('limite_inferior')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default DescuentosRangosSalariales;
